import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-829" />;
// Mock data structure for unlocked reports
interface UnlockedReport {
  id: string;
  type: 'self' | 'work' | 'relationship' | 'child';
  name: string;
  names?: {
    person1: string;
    person2: string;
  };
  date: string;
}
export function DecoderApp() {
  const [unlockedReports, setUnlockedReports] = useState<UnlockedReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Simulate loading unlocked reports from local storage
    const loadReports = () => {
      // Mock data - replace with actual storage check
      const mockReports: UnlockedReport[] = [];
      setUnlockedReports(mockReports);
      setIsLoading(false);
    };
    setTimeout(loadReports, 300);
  }, []);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'self':
        return 'Self System Diagnostic';
      case 'work':
        return 'Work System Diagnostic';
      case 'relationship':
        return 'Relationship Diagnostic';
      case 'child':
        return 'Child System Diagnostic';
      default:
        return 'Diagnostic';
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'self':
        return '🧬';
      case 'work':
        return '💼';
      case 'relationship':
        return '💞';
      case 'child':
        return '🧒';
      default:
        return '📄';
    }
  };
  const getReportPath = (report: UnlockedReport) => {
    switch (report.type) {
      case 'work':
        return `/report/work/${report.id}`;
      case 'relationship':
        return `/report/relationship/${report.id}`;
      case 'child':
        return `/report/child/${report.id}`;
      default:
        return `/report/${report.id}`;
    }
  };
  return <>
      <Helmet data-id="element-830">
        <title data-id="element-831">Decoder — Your Diagnostic Instrument</title>
        <meta name="description" content="Access your diagnostic instrument. View unlocked decodes or generate new pattern diagnostics." data-id="element-832" />
        <meta name="robots" content="noindex, nofollow" data-id="element-833" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-834">
        {/* Navigation */}
        <header data-id="element-835">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-836">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-837">
              <Link to="/app" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-838">
                <span className="text-lg font-serif" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-839">
                  Decoder
                </span>
              </Link>
              {unlockedReports.length > 0 && <a href="#unlocked-decodes" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-840">
                  Unlocked Decodes
                </a>}
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-841">
          {/* HERO SECTION */}
          <section className="relative py-20 md:py-32 px-6 overflow-hidden" data-id="element-842">
            <CoordinateGrid data-id="element-843" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-844">
              <div className="space-y-6" data-id="element-845">
                <h1 className="text-5xl md:text-6xl font-serif leading-none text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-846">
                  Decoder
                </h1>

                <p className="text-xl text-gray-400" data-id="element-847">
                  A diagnostic instrument for human patterns.
                </p>

                <p className="text-base text-gray-500" data-id="element-848">
                  It shows the loops you keep living inside.
                </p>
              </div>

              {/* Primary CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-8" data-id="element-849">
                <Link to="/reports" className="flex-1 px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center font-medium text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors" data-id="element-850">
                  Start a New Decode
                </Link>
                {unlockedReports.length > 0 && <a href="#unlocked-decodes" className="flex-1 px-8 py-4 border border-[#F5F5F5] text-[#F5F5F5] text-center font-medium text-sm uppercase tracking-widest hover:bg-[#F5F5F5]/10 transition-colors" data-id="element-851">
                    View Unlocked Decodes
                  </a>}
              </div>
            </div>
          </section>

          {/* UNLOCKED DECODES SECTION */}
          {!isLoading && <section id="unlocked-decodes" className="py-16 px-6 border-t border-[#2B2B2B]" data-id="element-852">
              <div className="max-w-4xl mx-auto" data-id="element-853">
                <h2 className="text-2xl font-medium text-[#F5F5F5] mb-8" data-id="element-854">
                  Your Unlocked Decodes
                </h2>

                {unlockedReports.length === 0 /* Empty State */ ? <div className="py-12 text-center space-y-8" data-id="element-855">
                    <div className="space-y-4" data-id="element-856">
                      <p className="text-lg text-gray-400" data-id="element-857">
                        You haven't unlocked any decodes yet.
                      </p>
                      <p className="text-base text-gray-500" data-id="element-858">
                        Start with a Self System Diagnostic.
                      </p>
                    </div>
                    <Link to="/start?product=self" className="inline-block px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-medium text-sm uppercase tracking-widest hover:bg-gray-200 transition-colors" data-id="element-859">
                      Start Your First Decode
                    </Link>
                  </div> /* List of Unlocked Reports */ : <div className="space-y-4" data-id="element-860">
                    {unlockedReports.map(report => <Link key={report.id} to={getReportPath(report)} className="block group" data-id="element-861">
                        <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] hover:border-[#64748B] transition-colors duration-300" data-id="element-862">
                          <div className="flex items-center justify-between gap-4" data-id="element-863">
                            <div className="flex items-center gap-4 flex-1" data-id="element-864">
                              <span className="text-2xl" data-id="element-865">
                                {getTypeIcon(report.type)}
                              </span>
                              <div className="space-y-1" data-id="element-866">
                                <p className="text-base text-[#F5F5F5]" data-id="element-867">
                                  {getTypeLabel(report.type)}
                                </p>
                                <p className="text-sm text-gray-500" data-id="element-868">
                                  {report.name} — {report.date}
                                </p>
                              </div>
                            </div>
                            <span className="px-6 py-2 border border-[#F5F5F5] text-[#F5F5F5] text-xs uppercase tracking-widest group-hover:bg-[#F5F5F5] group-hover:text-[#0A0A0A] transition-colors" data-id="element-869">
                              Open
                            </span>
                          </div>
                        </div>
                      </Link>)}
                  </div>}
              </div>
            </section>}

          {/* GENERATE NEW DECODE SECTION */}
          <section className="py-16 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-870">
            <div className="max-w-4xl mx-auto" data-id="element-871">
              <h2 className="text-2xl font-medium text-[#F5F5F5] mb-12" data-id="element-872">
                Generate a New Decode
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-873">
                {/* Self System Diagnostic */}
                <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] space-y-4" data-id="element-874">
                  <div className="flex items-center gap-3" data-id="element-875">
                    <span className="text-2xl" data-id="element-876">🧬</span>
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-877">
                      Self System Diagnostic
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-878">
                    Shows the pattern you keep living inside.
                  </p>
                  <Link to="/start?product=self" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-xs uppercase tracking-widest hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-879">
                    Start
                  </Link>
                </div>

                {/* Work System Diagnostic */}
                <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] space-y-4" data-id="element-880">
                  <div className="flex items-center gap-3" data-id="element-881">
                    <span className="text-2xl" data-id="element-882">💼</span>
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-883">
                      Work System Diagnostic
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-884">
                    Shows how your system operates in work contexts.
                  </p>
                  <Link to="/start?product=work" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-xs uppercase tracking-widest hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-885">
                    Start
                  </Link>
                </div>

                {/* Relationship Diagnostic */}
                <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] space-y-4" data-id="element-886">
                  <div className="flex items-center gap-3" data-id="element-887">
                    <span className="text-2xl" data-id="element-888">💞</span>
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-889">
                      Relationship Diagnostic
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-890">
                    Shows the loop a relationship is organized around.
                  </p>
                  <Link to="/start?product=relationship" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-xs uppercase tracking-widest hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-891">
                    Start
                  </Link>
                </div>

                {/* Child System Diagnostic */}
                <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] space-y-4" data-id="element-892">
                  <div className="flex items-center gap-3" data-id="element-893">
                    <span className="text-2xl" data-id="element-894">🧒</span>
                    <h3 className="text-lg font-medium text-[#F5F5F5]" data-id="element-895">
                      Child System Diagnostic
                    </h3>
                  </div>
                  <p className="text-sm text-gray-500" data-id="element-896">
                    Shows the structure a child is growing from.
                  </p>
                  <Link to="/start?product=child" className="block w-full px-6 py-3 border border-[#F5F5F5] text-[#F5F5F5] text-center text-xs uppercase tracking-widest hover:bg-[#F5F5F5] hover:text-[#0A0A0A] transition-colors" data-id="element-897">
                    Start
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* WHAT IS DECODER */}
          <section className="py-16 px-6 border-t border-[#2B2B2B]" data-id="element-898">
            <div className="max-w-3xl mx-auto text-center space-y-6" data-id="element-899">
              <p className="text-lg text-gray-400 leading-relaxed" data-id="element-900">
                Decoder is not therapy. It is not advice. It is not a
                personality test.
              </p>
              <p className="text-base text-[#F5F5F5]" data-id="element-901">
                It is a diagnostic instrument that shows the patterns and loops
                your life keeps running on.
              </p>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-902">
          <div className="max-w-6xl mx-auto" data-id="element-903">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-904">
              <div data-id="element-905">
                <p className="text-sm text-gray-500 font-mono" data-id="element-906">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-907">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-908">
                <Link to="/app" className="hover:text-[#F5F5F5] transition-colors" data-id="element-909">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-910">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-911">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-912">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}